import { CompanionPanel } from "@components/CompanionPanel";
import { useUser } from "@context/UserContext";
import {
  EmptyNote,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  PanelError,
  Skeleton,
  Tag,
} from "@scribenote/brian-design-system";
import { ResetPinButton } from "@users/ResetPinButton";
import { type ClinicUser, isLeadershipRole } from "@users/userClient";
import { useUsersList } from "@users/userQueries";
import { formatDate } from "@utils/format-date";

/** One person on the staff sheet: who they are, what they may do, their sign-in
 * code and the day they joined. The PIN reset is the only thing to be done to a
 * row, so it rides the end of the line. */
const UserRow = ({ user }: { user: ClinicUser }) => (
  <LedgerRow data-testid="admin-user-row">
    <LedgerCell>
      {/* Display names are unbounded free text, so the name is capped and
          truncated rather than pushing the rest of the line off the sheet. */}
      <span className="block max-w-[20rem] truncate font-medium text-ink">
        {user.displayName}
      </span>
    </LedgerCell>
    <LedgerCell>
      {/* One tag per role rather than a comma-joined line: a role is a fact
          about this person, and the column's question is what each of them may
          do here, so leadership is written in full ink and the rest quietly. */}
      <span className="flex max-w-[16rem] flex-wrap items-center gap-1">
        {user.roles.map((role) => (
          <Tag
            key={role.id}
            tone={isLeadershipRole(role) ? "emphasis" : "neutral"}
          >
            {role.name}
          </Tag>
        ))}
      </span>
    </LedgerCell>
    <LedgerCell mono className="text-annotation">
      {user.code}
    </LedgerCell>
    <LedgerCell mono className="whitespace-nowrap text-annotation">
      {formatDate(user.createdAt)}
    </LedgerCell>
    <LedgerCell align="right" className="w-px whitespace-nowrap">
      <ResetPinButton user={user} />
    </LedgerCell>
  </LedgerRow>
);

const UsersList = ({ sessionKey }: { sessionKey: string }) => {
  const { data: users } = useUsersList(sessionKey);

  if (users.length === 0)
    return <EmptyNote>Nobody on the roster yet.</EmptyNote>;

  return (
    <LedgerTable data-testid="admin-user-list">
      <LedgerHead>
        <tr>
          <LedgerHeadCell>Name</LedgerHeadCell>
          <LedgerHeadCell>Roles</LedgerHeadCell>
          <LedgerHeadCell>ID</LedgerHeadCell>
          <LedgerHeadCell>Joined</LedgerHeadCell>
          <LedgerHeadCell align="right" className="w-px">
            PIN
          </LedgerHeadCell>
        </tr>
      </LedgerHead>
      <LedgerBody>
        {users.map((u) => (
          <UserRow key={u.id} user={u} />
        ))}
      </LedgerBody>
    </LedgerTable>
  );
};

/** The ruled sheet before the names are written on it. */
const UsersSkeleton = () => (
  <div className="overflow-hidden rounded-paper border border-rule bg-card">
    {Array.from({ length: 4 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 border-b border-rule/50 p-3 last:border-b-0 even:bg-[color-mix(in_srgb,var(--color-wash-working)_16%,transparent)]"
      >
        <Skeleton index={i} className="h-4 flex-1" />
        <Skeleton index={i} className="h-4 w-24" />
      </div>
    ))}
  </div>
);

export const UsersPanel = () => {
  const { session } = useUser();
  const sessionKey = session?.userUuid ?? "";

  return (
    <CompanionPanel
      skeleton={<UsersSkeleton />}
      fallback={(retry) => <PanelError label="users" onRetry={retry} />}
    >
      <UsersList sessionKey={sessionKey} />
    </CompanionPanel>
  );
};
